import * as Sentry from '@sentry/react';

const SCRIPT_ID = 'google-maps-script';
const CALLBACK_NAME = '__foodamigosGoogleMapsLoaded';

let loading = null;

export const GOOGLE_MAPS_OPTIONS = {
  apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  libraries: ['places', 'geometry', 'drawing'],
  // Pinned to the last Maps JS version that still ships DrawingManager (removed in v3.65).
  // TODO: replace DrawingManager with a native drawing implementation, then drop this pin.
  version: '3.64',
};

/**
 * The Maps JS API URL, with the same parameters `@react-google-maps`' `useLoadScript` sent, except
 * for a callback name of our own instead of the shared global `initMap`.
 */
export function googleMapsScriptUrl({ apiKey, version, libraries }) {
  const params = new URLSearchParams({
    key: apiKey ?? '',
    v: version,
    libraries: [...libraries].sort().join(','),
    loading: 'async',
    callback: CALLBACK_NAME,
  });

  return `https://maps.googleapis.com/maps/api/js?${params.toString().replace(/%2C/g, ',')}`;
}

/**
 * Loads the Maps JS API once, app-wide, and resolves when `window.google.maps` is there.
 *
 * Replaces `useLoadScript`, which throws an invariant from a passive effect when its script
 * reports loaded but `window.google` is missing (DASHBOARD-WEB-C, an iOS app WebView). That throw
 * came from the root `App` component, so it unmounted the whole dashboard over a map most screens
 * never show. A failed or empty load now rejects instead, is logged, and is retried on the next
 * call; screens that need Maps keep their own handling of a missing `window.google`.
 */
export function loadGoogleMaps(options = GOOGLE_MAPS_OPTIONS) {
  if (window.google?.maps) {
    return Promise.resolve();
  }

  if (!loading) {
    loading = new Promise((resolve, reject) => {
      document.getElementById(SCRIPT_ID)?.remove();

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = googleMapsScriptUrl(options);
      script.async = true;
      script.onerror = () => reject(new Error('Google Maps script failed to load'));

      window[CALLBACK_NAME] = () => {
        if (window.google?.maps) {
          resolve();
        } else {
          reject(new Error('Google Maps script loaded, but window.google.maps is not present'));
        }
      };

      document.head.appendChild(script);
    }).catch((error) => {
      loading = null;
      Sentry.logger?.warn('google_maps_load_failed', { message: error.message });

      throw error;
    });
  }

  return loading;
}

/** Test seam: forget the cached load so each test starts cold. */
export function resetGoogleMapsLoaderForTests() {
  loading = null;
}
